import { useCallback, useEffect, useState } from "react";
import { BarChart3, Camera, ChevronDown, Plus, FileText, Image as ImageIcon, Film, Ruler, Sofa, Zap } from "lucide-react";
import api, { API } from "@/lib/api";
import { PrepReportDialog } from "@/components/PrepReportDialog";
import { PrepSupplierDialog } from "@/components/PrepSupplierDialog";
import { Row, Chip, reportChip, kindChip, Versions, ScheduleDialog, UploadBtn, downloadIcs, primary, second, dmy } from "@/components/PrepParts";

const EXTRA = [["planta", Ruler], ["home_staging", Sofa], ["perito_ce", Zap]];

function KindActions({ pid, k, st, ro, open, onDone }) {
  if (ro) return null;
  const who = k === "fotografo" ? "Pedir ao fotógrafo" : `Pedir ${k === "planta" ? "a planta" : k === "home_staging" ? "home staging" : "ao perito"}`;
  if (st.status === "por_pedir") return <button className={primary} onClick={() => open("ask", k)} data-testid={`prep-${k}-ask-btn`}>{who}</button>;
  if (st.status === "pedido") return (<>
    <button className={primary} onClick={() => open("schedule", k)} data-testid={`prep-${k}-schedule-btn`}>Marcar data</button>
    <button className={second} onClick={() => open("ask", k)} data-testid={`prep-${k}-ask-again-btn`}>Pedir de novo</button></>);
  if (st.status === "marcado") return (<>
    <a href={st.calendar} target="_blank" rel="noopener noreferrer" className={`${second} inline-flex items-center justify-center`} data-testid={`prep-${k}-cal-google`}>Adicionar ao calendário</a>
    <button className={second} onClick={() => downloadIcs(pid, k)} data-testid={`prep-${k}-cal-ics`}>Ficheiro .ics</button>
    <button className={primary} onClick={async () => { await api.post(`/processes/${pid}/prep/${k}/status`, { status: "recebidas" }); onDone(); }} data-testid={`prep-${k}-received-btn`}>
      {k === "fotografo" ? "Marcar como recebidas" : "Marcar como recebido"}</button></>);
  return <UploadBtn pid={pid} k={k} label={k === "fotografo" ? "Carregar fotografias e vídeo" : "Carregar ficheiros"} onDone={onDone} />;
}

function KindRow({ pid, k, s, icon, ro, open, onDone, small }) {
  const st = s.kinds[k];
  if (k === "perito_ce" && s.ce_existing) {
    const ce = s.ce_existing;
    return <Row small icon={icon} title="Certificado energético" testid="prep-perito_ce" chip={<Chip tone="green" testid="prep-perito_ce-status">Já existe</Chip>}
      sub={`Já existe${ce.classe ? ` (classe ${ce.classe}` : " ("}${ce.valid_until ? `${ce.classe ? ", " : ""}válido até ${dmy(ce.valid_until)}` : ""})`} />;
  }
  const [chip, sub] = kindChip(k, st);
  return <Row small={small} icon={icon} title={st.label} chip={chip} sub={sub} testid={`prep-${k}`}><KindActions pid={pid} k={k} st={st} ro={ro} open={open} onDone={onDone} /></Row>;
}

function Extras({ pid, s, ro, open, onDone }) {
  const busy = EXTRA.some(([k]) => s.kinds[k].status !== "por_pedir");
  const [show, setShow] = useState(busy);
  const ce = s.ce_existing ? ` · certificado energético: já existe${s.ce_existing.classe ? ` (classe ${s.ce_existing.classe})` : ""}` : "";
  return (
    <div className="py-3" data-testid="prep-extras">
      <button onClick={() => setShow(!show)} className="w-full flex items-start gap-3 text-left" aria-expanded={show} data-testid="prep-extras-toggle">
        <Plus size={20} className="text-slate-400 shrink-0 mt-0.5" />
        <span className="flex-1 min-w-0"><span className="block text-[15px] text-slate-600">Planta · Home staging · Certificado energético</span>
          {ce && <span className="block text-sm text-slate-400">{ce.slice(3)}</span>}</span>
        <ChevronDown size={16} className={`text-slate-400 mt-1 transition-transform ${show ? "rotate-180" : ""}`} />
      </button>
      {show && <div className="mt-1 pl-1 md:pl-8 divide-y divide-slate-100">{EXTRA.map(([k, icon]) => <KindRow key={k} pid={pid} k={k} s={s} icon={icon} ro={ro} open={open} onDone={onDone} small />)}</div>}
    </div>
  );
}

const ICON = (t) => (t === "application/pdf" ? FileText : t?.startsWith("video/") ? Film : ImageIcon);
const KIND_LBL = { relatorio: "Relatório de avaliação", fotografo: "Fotografias e vídeo", planta: "Planta", home_staging: "Home staging", perito_ce: "Certificado energético" };

function MediaSection({ media }) {
  return (
    <section className="bg-white rounded-[18px] border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] px-[18px] md:px-6 py-4" data-testid="media-section">
      <h2 className="text-[16px] font-semibold text-slate-900">Media e marketing</h2>
      {!media.length && <p className="text-sm text-slate-500 mt-1" data-testid="media-empty">As fotografias e o relatório aparecem aqui quando chegarem. Não entram nos documentos legais.</p>}
      <ul className="mt-2 divide-y divide-slate-100">{media.map((f) => { const I = ICON(f.content_type); return (
        <li key={f.id} className="py-2.5 flex items-center gap-3" data-testid={`media-file-${f.id}`}>
          <I size={18} className="text-slate-400 shrink-0" />
          <a href={`${API}/files/${f.id}`} target="_blank" rel="noopener noreferrer" className="flex-1 min-w-0 truncate text-[15px] text-slate-800 hover:underline">{f.filename}</a>
          <span className="text-xs text-slate-500 shrink-0 hidden sm:block">{KIND_LBL[f.media_kind]}</span>
        </li>); })}</ul>
    </section>
  );
}

export function PrepararVenda({ pid, ro }) {
  const [s, setS] = useState(null);
  const [dlg, setDlg] = useState(null);
  const load = useCallback(() => { api.get(`/processes/${pid}/prep`).then(({ data }) => setS(data)).catch(() => setS(null)); }, [pid]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (s && window.location.hash === "#sec-preparar") { setTimeout(() => document.getElementById("sec-preparar")?.scrollIntoView({ behavior: "smooth", block: "start" }), 200); }
  }, [s]);
  if (!s) return null;
  const off = ro || s.closed;
  const open = (type, k) => setDlg({ type, k });
  const [rChip, rSub] = reportChip(s.report);
  return (
    <div className="space-y-3">
      <section id="sec-preparar" className="bg-white rounded-[18px] border-2 border-emerald-400/70 shadow-[0_8px_30px_rgb(0,0,0,0.04)] px-[18px] md:px-6 pt-4 pb-1" data-testid="prep-card">
        <h2 className="text-[18px] font-semibold text-slate-900" data-testid="prep-title">Preparar a venda</h2>
        <div className="divide-y divide-slate-100">
          <Row icon={BarChart3} title="Relatório de avaliação" chip={rChip} sub={rSub} testid="prep-report">
            {!off && <button className={s.report.versions.length ? second : primary} onClick={() => open("report")} data-testid="prep-report-btn">
              {s.report.versions.length ? "Enviar nova versão" : "Enviar relatório ao proprietário"}</button>}
          </Row>
          {s.report.versions.length > 1 && <div className="pb-3 pl-[34px]"><Versions vs={s.report.versions} /></div>}
          <KindRow pid={pid} k="fotografo" s={s} icon={Camera} ro={off} open={open} onDone={load} />
          <Extras pid={pid} s={s} ro={off} open={open} onDone={load} />
        </div>
      </section>
      <MediaSection media={s.media} />
      {dlg?.type === "report" && <PrepReportDialog pid={pid} s={s} open onClose={() => setDlg(null)} onDone={load} />}
      {dlg?.type === "ask" && <PrepSupplierDialog pid={pid} k={dlg.k} s={s} onClose={() => setDlg(null)} onDone={load} />}
      {dlg?.type === "schedule" && <ScheduleDialog pid={pid} k={dlg.k} st={s.kinds[dlg.k]} onClose={() => setDlg(null)} onDone={load} />}
    </div>
  );
}
